import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import LRUCache from 'lru-cache';

import { Account } from '@mitel/cloudlink-sdk';
import { AccountsService } from './accounts.service';
import { AsyncJobsService } from './async-jobs.service';
import { CloudLinkNotifyService } from './cloudlink-notify.service';
import { ExternalRefreshNotifierService } from './external-refresh-notifier.service';
import { IntegUnifyPhoneService } from './integ-unify-phone.service';
import { SystemManagerService } from './system-manager.service';
import { TenantsService } from './tenants.service';
import { Tenant, TenantType, TenantSipTrunk, BillingAsyncJobType,  } from './../interfaces/tenants';
import { Application } from '../interfaces/system-manager';
import { SystemManagerUtil } from '../utils/system-manager-util';
import { ExternalIntegrationActionType } from './../interfaces/integrations';
import { asyncJobOwner, asyncJobStatus, NotifyAlertType, productCodes, ApplicationType } from './../constants/constants';
import { Util } from './../utils/Util';

const logContext = 'IntegUnifyPhoneSharedService';
@Injectable({
  providedIn: 'root'
})
export class IntegUnifyPhoneSharedService {

  // store tenant for account if exists, tenantId is not saved in account tags
  private tenantsCache = new LRUCache<string, Tenant>({
    ttl: 60 * 10 * 1000,  // ms (cache item expires in 10 min)
    max: 10          // and, oldest item in cache is bumped if more than this items in cache
    // https://github.com/isaacs/node-lru-cache#readme
  }); // cache by account ID

  // store applications needed to establish SIP connectivity with the Unify Platform
  // MiVoice 5000 / MiVoice Business + MBG
  private applicationsCache = new LRUCache<string, Application[]>({
    ttl: 60 * 10 * 1000,  // ms (cache item expires in 10 min)
    max: 10          // and, oldest item in cache is bumped if more than this items in cache
    // https://github.com/isaacs/node-lru-cache#readme
  }); // cache by account ID

  // store SIP trunks to determine integration status
  private sipTrunksCache = new LRUCache<string, TenantSipTrunk[]>({
    ttl: 60 * 10 * 1000,  // ms (cache item expires in 10 min)
    max: 10          // and, oldest item in cache is bumped if more than this items in cache
    // https://github.com/isaacs/node-lru-cache#readme
  }); // cache by account ID

  constructor(
    private accountsService: AccountsService,
    private asyncJobsService: AsyncJobsService,
    private cloudLinkNotifyService: CloudLinkNotifyService,
    private externalRefreshNotifierService: ExternalRefreshNotifierService,
    private integUnifyPhoneService: IntegUnifyPhoneService,
    private systemManagerService: SystemManagerService,
    private tenantsService: TenantsService,
    private translateService: TranslateService) {
      this.cloudLinkNotifyService.eventNotification$.subscribe(async event => {
        console.log(`${logContext}.eventNotification$ : content after get `, event);
        if (event.topic == 'platform-api-billing' && event.content?.clientJobTag?.tenantType == TenantType.UNIFY_PHONE) {
          this.handleBillingAsyncEvent(event);
        } else {
          console.log(`${logContext} eventNotification$ ignored - not a Unify billing async event`);
        }
      });
  }

  async canConfigureSIPTrunks(account: Account): Promise<boolean> {
    let applications = await this.getSIPApplications(account);
    return Boolean(applications?.length);
  }

  async getTenant(account: Account): Promise<Tenant | undefined> {
    if (!this.tenantsCache.has(account.accountId)) {
      try {
        const tenant = await this.tenantsService.getTenantByType(TenantType.UNIFY_PHONE);
        this.tenantsCache.set(account.accountId, tenant);
      }
      catch (error) {
        this.tenantsCache.delete(account.accountId);
        console.error(`${logContext}.getTenant - Error determining Unify Phone tenant`, error);
        throw error;
      }
    }
    return this.tenantsCache.get(account.accountId);
  }

  addTenantToCache(account: Account, tenant: Tenant) {
    if (!this.tenantsCache.has(account.accountId)) {
      this.tenantsCache.set(account.accountId, tenant);
    }
  }

  deleteTenantFromCache(account: Account) {
    if (this.tenantsCache.has(account.accountId)) {
      this.tenantsCache.delete(account.accountId);
    }
  }

  async getSIPApplications(account: Account): Promise<Application[]> {
    if (!this.applicationsCache.has(account.accountId)) {
      try {
        const applications = await this.getApplications();
        this.applicationsCache.set(account.accountId, applications);
      }
      catch (error) {
        this.applicationsCache.delete(account.accountId);
        console.error(`${logContext}.getSIPApplications - Error determining system manager - applications`, error);
        throw error;
      }
    }
    return this.applicationsCache.get(account.accountId);
  }

  async getApplications(): Promise<Application[]> {

    let applications = [];
    // expand: 'components,tags'
    // components: to determine if PBX is compatible with the Unify Platform
    // tags: machine tag stores IP address inside 'networkServices'
    const platformsEmbedded = await this.systemManagerService.getPlatforms({ $expand: 'components,tags' });
    if (platformsEmbedded?.length) {
      // flatten platform.applications
      // look for applications needed to establish SIP connectivity with the Unify Platform
      // MiVoice 5000 / MiVoice Business + MBG
      const applicationTypes = Object.values(ApplicationType);
      applications = SystemManagerUtil.processPlatformsData(platformsEmbedded)
        .map(platform => platform.applications)
        .flat()
        .filter(application => applicationTypes.indexOf(application.applicationType as ApplicationType) > -1);
    }

    return applications;
  }

  async getSIPTrunks(account: Account): Promise<TenantSipTrunk[]> {
    if (!this.sipTrunksCache.has(account.accountId)) {
      try {
        const sipTrunks = await this.getTenantSIPTrunks(account);
        this.sipTrunksCache.set(account.accountId, sipTrunks);
      }
      catch (error) {
        this.sipTrunksCache.delete(account.accountId);
        console.error(`${logContext}.getSIPTrunks - Error determining SIP trunks for account`, error);
        throw error;
      }
    }
    return this.sipTrunksCache.get(account.accountId);
  }

  async getTenantSIPTrunks(account: Account): Promise<TenantSipTrunk[]> {

    try {
      const tenant = await this.getTenant(account);

      if(tenant?.tenantId) {
        return await this.tenantsService.getTenantSipTrunks(tenant.tenantId);
      } else {
        console.error(`${logContext}.getTenantSipTrunks - Error getting SIP Trunks: Missing tenant for account`);
        throw 'Missing tenant for account';
      }
    } catch (error) {
      console.error(`${logContext}.getTenantSipTrunks - Error getting SIP Trunks `, error);
      throw error;
    }
  }

  deleteSIPTrunksFromCache(account: Account) {
    if (this.sipTrunksCache.has(account.accountId)) {
      this.sipTrunksCache.delete(account.accountId);
    }
  }

  async handleBillingAsyncEvent(event) {
    if (!event?.content)
      return; // nothing that we can do

    if (event?.content?.acknowledged == true)
      return; // do not react to the acknowledged event

    // update the notification list first - does not matter the job type
    // if the job was already acknowledged, then nothing to update
    let jobUpdate = {
      jobType: event.content.jobType,
      jobId: event.content.jobId,
      status: event.content.status,
      owner: asyncJobOwner.UNIFY_PHONE,
      createdOn: new Date(event.content.createdOn),
      modifiedOn: new Date(event.content.modifiedOn),
      clientJobTag: event.content.clientJobTag
    };

    this.asyncJobsService.handleTenantAsyncJobChangeEvent(jobUpdate);

    // rest is async delete handling
    if (event.content.jobType == BillingAsyncJobType.TENANT_DELETION
      && (event.content.status == asyncJobStatus.COMPLETE || event?.content.status == asyncJobStatus.FAILED)) {
      const account = this.accountsService.getCurrentAccount();
      // there has been a change to the status of the delete operation
      // if it is complete, just complete the delete integration.
      let action: ExternalIntegrationActionType;
      let message: string = '';
      let type: NotifyAlertType;

      switch (event.content.status) {
        case asyncJobStatus.COMPLETE:
          await this.integUnifyPhoneService.asyncJobIsComplete(account, event.content.jobId);
          action = ExternalIntegrationActionType.DELETE;
          message = this.translateService.instant('integrations.unify-phone.delete.success');
          type = NotifyAlertType.SUCCESS;
          break;
        case asyncJobStatus.FAILED:
          const retMsg = Util.extractMessageFromError(event.content.results);
          await this.integUnifyPhoneService.asyncJobFailed(account, event.content.jobId, retMsg || event.content.results);
          action = ExternalIntegrationActionType.UPDATE;
          message = this.translateService.instant('integrations.unify-phone.delete.failed') + (retMsg ? ' ' + retMsg : '');
          type = NotifyAlertType.DANGER;
          break;
        default:
          break;
      }

      const config = {
        message: message,
        actionString: 'integrations.unify-phone.delete.refreshLink',
        type: type,
        emittedData: {
          featureId: 'IntegrationChange',
          data: {
            action: action,
            productCode: productCodes.UNIFY_PHONE
          }
        }
      };
      this.externalRefreshNotifierService.externalSourceRequestsRefresh(config);
    }
  }
    
}
